import { useState, useMemo, useRef, useEffect } from "react";
import { Pressable, TextInput } from "react-native";
import { View, Text, FormInput } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";
import { RightDrawer } from "./RightDrawer.web";
import { useTenant, getTenantDisplayName, filterTenants } from "@src/features/tenants";

interface TenantSwitcherPanelProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Right drawer panel for switching between tenants.
 * Includes search/filter and highlights the current selection.
 */
export function TenantSwitcherPanel({ visible, onClose }: TenantSwitcherPanelProps) {
  const { theme } = useUnistyles();
  const { tenants, selectedTenant, selectTenant } = useTenant();
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<TextInput>(null);

  const filteredTenants = useMemo(() => filterTenants(tenants, searchQuery), [tenants, searchQuery]);

  useEffect(() => {
    if (!visible) {
      setSearchQuery("");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  const handleSelectTenant = (tenantId: string) => {
    if (tenantId !== selectedTenant?.id) {
      selectTenant(tenantId);
    }
    onClose();
  };

  return (
    <RightDrawer visible={visible} onClose={onClose} title="Switch Organisation">
      <View testID="tenant-switcher-panel">
        <View style={{ marginBottom: theme.spacing["3xl"] }}>
          <FormInput
            ref={searchInputRef}
            placeholder="Search organisations"
            value={searchQuery}
            onChangeText={setSearchQuery}
            size="medium"
            clearable
          />
        </View>

        {filteredTenants.length === 0 ?
          <View
            style={{
              paddingVertical: theme.spacing["6xl"],
              alignItems: "center",
            }}
          >
            <Text textStyle="sm/regular" color="textMuted">
              No organisations found
            </Text>
          </View>
        : filteredTenants.map((tenant) => {
            const isSelected = tenant.id === selectedTenant?.id;

            return (
              <Pressable
                key={tenant.id}
                testID={`tenant-option-${tenant.id}`}
                onPress={() => handleSelectTenant(tenant.id)}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: theme.spacing["3xl"],
                  borderBottomWidth: 0.5,
                  borderBottomColor: theme.colors.borderCard,
                }}
              >
                <View flex={1}>
                  <Text textStyle="sm/regular" color={isSelected ? "text" : "textMuted"}>
                    {getTenantDisplayName(tenant)}
                  </Text>
                </View>
                {isSelected && <Icon name="checkmark" size={20} color={theme.colors.text} />}
              </Pressable>
            );
          })
        }
      </View>
    </RightDrawer>
  );
}
